import { useForm } from '@mantine/form';
import {
  FormEvent,
  useEffect,
  useRef
} from 'react';
import { MessageProps } from '@app/form_components';
import { FormControlProps } from '@app/types';

export type UseValueControlProps<TValue> = (
  Pick<FormControlProps<TValue>, (
    'name'
    | 'onBlur'
    | 'onChange'
    | 'onFocus'
  )>
  & {
    /** Error for this control set by @see UseValueFormResult.setErrors or @see UseValueFormProps.validate. */
    error?: MessageProps['message'];
    value: TValue;
  }
);

export type UseValueFormErrors<TValues> = Partial<Record<
  keyof(TValues),
  MessageProps['message']
>>;

export type UseValueFormProps<TValues, TTransform = TValues> = {
  /** A name prefix applied to each field's `name` prop via `getInputProps`. */
  name?: string;
  /** Saved values to replace the form's values with when it changes. */
  savedValues: TValues;
  /** Transform values before they are submitted or if you getValues. */
  transformValues?: (values: TValues) => TTransform;
  /** Validate values on submit. Return an errors object; fields with no error should be omitted or set to null. */
  validate?: (values: TValues) => UseValueFormErrors<TValues>;
  /** Decides if @see validate runs on control value change or not @default false. */
  validateControlOnChange?: boolean;
  /** Decides if @see validate is ran on the initial value before it is changed @default false. */
  validateOnInit?: boolean;
};

export type UseValueFormResult<TValues, TTransform = TValues> = {
  errors: UseValueFormErrors<TValues>;
  getControlProps: <K extends keyof(TValues) & string>(key: K) => (
    UseValueControlProps<TValues[K]>
  );
  getValues: () => TTransform;
  createSubmitHandler: (
    handleSubmit?: (values: TTransform, event: FormEvent<HTMLFormElement>
  ) => void) => (
    (event?: FormEvent<HTMLFormElement>) => void
  );
  setErrors: (newErrors: UseValueFormErrors<TValues>) => void;
};

export const useValueForm = <TValues, TTransform = TValues>({
  name,
  savedValues,
  transformValues,
  validate,
  validateControlOnChange,
  validateOnInit
}: UseValueFormProps<TValues, TTransform>): (
  UseValueFormResult<TValues, TTransform>
) => {
  type FormKey = keyof(TValues);

  const form = useForm<TValues, (values: TValues) => TTransform>({
    initialValues: savedValues,
    transformValues,
    validateInputOnChange: validateControlOnChange,
    validate
  });
  const formValues = useRef(form.values);
  formValues.current = form.values;
  const validateRef = useRef(validate);
  validateRef.current = validate;

  useEffect(() => {
    form.setValues(savedValues);
    if (validateRef.current) {
      form.setErrors(validateRef.current(savedValues));
    }
  }, [savedValues]);

  useEffect(
    () => {
      if (validateOnInit && validate) {
        form.setErrors(validate(form.values));
      }
    },
    // Intentionally empty: validate against the initial values exactly once on mount.
    []
  );

  const getControlProps = <K extends FormKey & string>(
    key: K
  ): UseValueControlProps<TValues[K]> => {
    const { checked: _, ...inputProps } = form.getInputProps(key);

    return {
      ...inputProps,
      name: (name) ? `${name}-${key}` : key
    };
  };

  return {
    createSubmitHandler: (handleSubmit) => {
      return form.onSubmit((values, event) => {
        event.stopPropagation();
        handleSubmit?.(values, event);
      });
    },
    errors: form.errors as UseValueFormErrors<TValues>,
    getControlProps,
    getValues: () => form.getTransformedValues(formValues.current),
    setErrors: form.setErrors
  };
};
